/**
 * Words and numbers for the CPU and memory an agent measured during a scan
 * (release 3.2 telemetry item 2). Pure, so the rules are tested without a
 * browser:
 *
 * - absent is "not measured", never 0 (the rule of edge-measurement-format.ts);
 * - CPU is percent of the WHOLE machine, the unit of the CPU ceiling, so the
 *   two are compared directly;
 * - the verdict is the measurement's, said plainly: the busiest 5-second
 *   window below the cap, or at or over it, with both numbers.
 */

import type { EdgeScanResources } from '@/lib/api';

import { NOT_REPORTED } from './edge-measurement-format';

const INDIAN = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

export function formatPercent(v: number | undefined | null): string {
  return v == null ? NOT_REPORTED : `${INDIAN.format(v)}%`;
}

export function formatMemory(mb: number | undefined | null): string {
  if (mb == null) return NOT_REPORTED;
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GiB` : `${INDIAN.format(mb)} MiB`;
}

/** "4 min 05 s", "1 h 12 min", "0.4 s". */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return NOT_REPORTED;
  if (seconds < 10) return `${seconds.toFixed(1)} s`;
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
  if (m > 0) return `${m} min ${String(sec).padStart(2, '0')} s`;
  return `${sec} s`;
}

export type CeilingVerdict = 'under' | 'reached' | 'no_ceiling' | 'not_measured';

/**
 * The busiest 5-second window against the cap in force, as measured: below it,
 * or at or over it. No tolerance band (review of PR #146, M3): a window that
 * reads over the cap is shown as over it, with both numbers, and the method
 * line says why a window can read over a cap that the operating system
 * enforces over shorter intervals.
 */
export function ceilingVerdict(r: EdgeScanResources): CeilingVerdict {
  if (r.cpuPeakPercent == null) return 'not_measured';
  if (r.cpuCeilingPercent == null || r.cpuCeilingPercent <= 0) return 'no_ceiling';
  return r.cpuPeakPercent < r.cpuCeilingPercent ? 'under' : 'reached';
}

const POINTS = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

/** What was measured, in words, with the numbers side by side. */
export function verdictText(r: EdgeScanResources): string {
  const c = r.cpuCeilingPercent;
  const peak = formatPercent(r.cpuPeakPercent);
  const avg = formatPercent(r.cpuAvgPercent);
  switch (ceilingVerdict(r)) {
    case 'under':
      return `Busiest 5-second window ${peak} against a ${c}% cap; average ${avg}.`;
    case 'reached': {
      const over = (r.cpuPeakPercent ?? 0) - (c ?? 0);
      return over > 0
        ? `Busiest 5-second window ${peak} against a ${c}% cap (${POINTS.format(over)} points over); average ${avg}.`
        : `Busiest 5-second window ${peak}, at the ${c}% cap; average ${avg}.`;
    }
    case 'no_ceiling':
      return `No CPU cap was in force for this scan. Busiest 5-second window ${peak}; average ${avg}.`;
    case 'not_measured':
      return 'The scan was too short to measure a CPU rate; its CPU time and memory are shown.';
  }
}

export function scopeText(scope: string): string {
  switch (scope) {
    case 'agent_and_workers':
      return 'the agent and its document workers';
    case 'agent_only':
      return 'the agent process only (its document workers are not counted on this platform)';
    default:
      return scope.replace(/_/g, ' ');
  }
}

/** "Measured every 5 s over 4 min 05 s, as % of the whole machine (8 logical CPUs): the agent and its document workers." */
export function methodText(r: EdgeScanResources): string {
  return (
    `Measured every ${r.sampleSeconds} s over ${formatDuration(r.wallSeconds)}, ` +
    `as % of the whole machine (${r.logicalCpus} logical CPU${r.logicalCpus === 1 ? '' : 's'}): ${scopeText(r.scope)}.`
  );
}

/**
 * How the cap is enforced, as documented, not as measured: why a 5-second
 * window can read a little over a cap that held.
 */
export const CAP_NOTE =
  'The operating system enforces the cap over its own short scheduling intervals, not over these 5-second ' +
  'windows (Windows job objects: JOBOBJECT_CPU_RATE_CONTROL_INFORMATION, Microsoft documentation; Linux: the ' +
  "cgroup's cpu.max period), so a single window can read slightly over the cap.";

/** Why this is not the check-in reading shown above it. */
export const NOT_THE_CHECKIN_READING =
  'Not comparable with the check-in reading above: that is the agent process alone, as % of one core, ' +
  'taken once at check-in.';
